import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { REACT_APP_API_GATEWAY_URL_CONTACT, REACT_APP_TURNSTILE_SITE_KEY } from '../../../constants/config';

const Turnstile = lazy(() => import('@marsidev/react-turnstile').then((mod) => ({ default: mod.Turnstile })));

const SignupForm = () => {
  const [email, setEmail] = useState('');
  const [feedback, setFeedback] = useState({ message: '', classes: [] });
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  const formFeedback = (msg, additionalClasses) => {
    return { message: msg, classes: additionalClasses };
  };

  const handleTurnstileSuccess = useCallback((token) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const handleTurnstileExpired = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!turnstileToken) {
      setFeedback(formFeedback('Please complete the verification', ['error']));
      return;
    }

    setFeedback(formFeedback('Submitting request...', ['pending']));

    try {
      const response = await fetch(REACT_APP_API_GATEWAY_URL_CONTACT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, action: 'quadrant', turnstileToken }),
      });

      if (!response.ok) {
        throw new Error('Server error');
      }

      const responseData = await response.json();
      if (!responseData.success) {
        throw new Error(responseData.message);
      }

      setEmail('');
      setTurnstileToken(null);
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setFeedback(formFeedback("We've received your request—thank you!", ['success']));
    } catch (error) {
      console.error('Error on server side:', error);
      setEmail('');
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setTurnstileToken(null);
      setFeedback(formFeedback('There was an error submitting your request: ' + error.message, ['error']));
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      document.body.classList.add('loaded');
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div id="signup" className="section" style={{ width: '100%' }}>
      <div className="form-container">
        <form id="contact-form" className="waitlist-form" method="POST" onSubmit={handleSubmit}>
          <input
            type="email"
            id="quadrant-email-input"
            name="email"
            placeholder="Enter your work email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <div className="glass-btn">
            <button id="submit" type="submit" className="bg-black text-white font-semibold py-4 px-8 hover:bg-black">
              Find out where you stand
            </button>
          </div>
          {email.trim() && (
            <div>
              <Suspense
                fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                }
              >
                <Turnstile
                  ref={turnstileRef}
                  siteKey={REACT_APP_TURNSTILE_SITE_KEY}
                  onSuccess={handleTurnstileSuccess}
                  onError={handleTurnstileError}
                  onExpire={handleTurnstileExpired}
                />
              </Suspense>
            </div>
          )}
        </form>
        {feedback.message && (
          <div id="form-feedback" className={`row justify-content-center form-feedback ${feedback.classes.join(' ')}`}>
            {feedback.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default SignupForm;
